<template>
    <div class="tw-bg-white tw-p-4 tw-py-3 tw-border tw-border-solid tw-border-gray-200 tw-rounded">
        <div class="tw-flex tw-items-center tw-mb-6 tw-gap-3">
            <div class="tw-flex tw-items-center tw-gap-2">
                <p class="tw-p-2x tw-font-bold tw-text-lg">{{ $t('common.top_products') }}</p>
            </div>
        </div>

        <div v-if="loading" class="tw-space-y-4">
            <div v-for="i in 4" :key="i" class="tw-bg-gray-100 tw-rounded tw-p-4 tw-animate-pulse">
                <div class="tw-bg-gray-200 tw-h-4 tw-w-1/3 tw-mb-2 tw-rounded"></div>
                <div class="tw-bg-gray-200 tw-h-3 tw-w-full tw-mb-2 tw-rounded"></div>
                <div class="tw-bg-gray-200 tw-h-3 tw-w-2/3 tw-rounded"></div>
            </div>
        </div>

        <div v-else-if="products.length === 0" class="tw-text-center tw-py-8">
            <p class="tw-text-gray-500">{{ $t('common.no_data_available') }}</p>
        </div>

        <div v-else>
            <div class="tw-space-y-3">
                <div v-for="product in displayedProducts" :key="product.id" class="tw-p-3 tw-bg-gray-50 tw-rounded-lg">
                    <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <h3 class="tw-font-semibold tw-text-sm">{{ product.name }}</h3>
                        <span class="tw-bg-emerald-100 tw-text-emerald-700 tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium">
                            {{ product.reconfirmation_rate }}% {{ $t('common.reconfirmed') }}
                        </span>
                    </div>
                    
                    <div class="tw-flex tw-items-center tw-gap-4 tw-text-xs tw-text-gray-600">
                        <span class="tw-flex tw-items-center tw-gap-1">
                            <span class="tw-w-2 tw-h-2 tw-bg-blue-500 tw-rounded-full"></span>
                            {{ $t('common.total_followup_orders') }}: {{ product.total_followup_orders }}
                        </span>
                        <span class="tw-flex tw-items-center tw-gap-1">
                            <span class="tw-w-2 tw-h-2 tw-bg-emerald-500 tw-rounded-full"></span>
                            {{ $t('common.reconfirmed') }}: {{ product.reconfirmed_orders }}
                        </span>
                        <span class="tw-flex tw-items-center tw-gap-1">
                            <span class="tw-w-2 tw-h-2 tw-bg-purple-500 tw-rounded-full"></span>
                            {{ $t('common.delivered') }}: {{ product.delivered_after_reconfirmation }}
                        </span>
                        <span class="tw-flex tw-items-center tw-gap-1">
                            <span class="tw-w-2 tw-h-2 tw-bg-rose-500 tw-rounded-full"></span>
                            {{ $t('common.delivery_rate') }}: {{ product.delivered_rate_after_reconfirmation }}%
                        </span>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="products.length > itemsPerPage" class="tw-flex tw-items-center tw-justify-center tw-mt-4 tw-gap-2">
                <button @click="previousPage" :disabled="currentPage === 1" class="tw-px-3 tw-py-1 tw-text-xs tw-border tw-rounded tw-disabled:opacity-50">
                    {{ $t('common.previous') }}
                </button>
                <span class="tw-text-xs tw-text-gray-500">
                    {{ currentPage }} / {{ totalPages }}
                </span>
                <button @click="nextPage" :disabled="currentPage === totalPages" class="tw-px-3 tw-py-1 tw-text-xs tw-border tw-rounded tw-disabled:opacity-50">
                    {{ $t('common.next') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, inject, watch } from 'vue';
import { followupManager } from '@/api/dashboard';

const loading = ref(true);
const products = ref([]);
const currentPage = ref(1);
const itemsPerPage = 4;
const filters = inject('filters');

const totalPages = computed(() => Math.ceil(products.value.length / itemsPerPage));

const displayedProducts = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return products.value.slice(start, end);
});

const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const previousPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};



const getData = async () => {
    loading.value = true;
    try {
        const params = {
            ...filters.value,
            ...(filters.value.followup_id && filters.value.followup_id.length > 0 && { followup_id: filters.value.followup_id })
        }
        const response = await followupManager.topProducts(params);
        if (response.data.code === 'SUCCESS') {
            // API response structure: response.data.data.data contains the products array
            products.value = Array.isArray(response.data.data?.data) ? response.data.data.data : [];
        }
    } catch (error) {
        console.error('Error fetching top products:', error);
        products.value = [];
    } finally {
        loading.value = false;
    }
}

// Watch for filter changes to refresh data
watch(() => filters.value, () => {
    getData()
    currentPage.value = 1; // Reset to first page when filters change
}, { deep: true })

getData();
</script> 